import { useState } from 'react'
import { usePromotionsData } from '../hooks/usePromotionsData.js'
import { useMarketingDrafts } from '../hooks/useMarketingDrafts.js'
import { useHeroBrief } from '../hooks/useHeroBrief.js'
import { formatMoney } from '../lib/format.js'
import AskAiBar from './AskAiBar.jsx'
import HeroBriefCard from './HeroBriefCard.jsx'
import { Skeleton, SkeletonCard, SkeletonList } from './Skeleton.jsx'
import StatusPill from './StatusPill.jsx'
import { toneFor, labelFor } from '../lib/pillStatus.js'
import PromotionEditModal from './PromotionEditModal.jsx'
import PromotionPostsModal from './PromotionPostsModal.jsx'
import MarketingDraftsInbox from './MarketingDraftsInbox.jsx'
import MarketingSettingsModal from './MarketingSettingsModal.jsx'
import ReviewReplyCard from './ReviewReplyCard.jsx'
import Toast from './Toast.jsx'

// Marketing screen — three sub-tabs:
//   Promotions  — promotion tracker + per-promo AI post generator
//                 (the existing live/planned/past view).
//   Drafts      — auto-generated post inbox. Customer's weekly
//                 review queue from generate-marketing-drafts.
//   Reviews     — Google review reply assistant card. Compact
//                 enough to share the Promotions tab in practice,
//                 but lives here so customers can find it.
//
// Settings (cadence, tone, platforms, voice notes) opens as a
// modal from inside the Drafts tab — there's no separate sub-tab
// for settings since it's an infrequent action.
export default function PromotionsScreen() {
  const {
    grouped, restaurant, loading,
    createPromotion, updatePromotion, setStatus, deletePromotion,
  } = usePromotionsData()

  const drafts = useMarketingDrafts()

  const [tab, setTab] = useState('promotions')          // 'promotions' | 'drafts'
  const [editingPromo, setEditingPromo]     = useState(null)
  const [createOpen, setCreateOpen]         = useState(false)
  const [generatingFor, setGeneratingFor]   = useState(null)  // promotion currently in posts modal
  const [settingsOpen, setSettingsOpen]     = useState(false)
  const [toast, setToast]                   = useState('')

  // Marketing hero brief — sits above the screen header. Phase 2
  // pattern matches the other hubs: charcoal card, sub-aware (no
  // sub here — Marketing is one tab with sub-views inside its body).
  //
  // MUST be called before any early return below — React hooks rule.
  // (Earlier draft placed this after the loading-skeleton return,
  // which crashed Marketing with React #310 once `loading` flipped.)
  const hero = useHeroBrief('marketing', null, { enabled: true })

  if (loading && grouped.live.length === 0 && grouped.planned.length === 0 && grouped.past.length === 0) {
    return (
      <div className="min-h-full bg-white pb-12">
        <main className="mx-auto max-w-screen-sm">
          <SkeletonCard />
          <section className="mx-4 mt-6">
            <Skeleton className="h-4 w-32" />
            <SkeletonList rows={3} className="mt-2" />
          </section>
        </main>
      </div>
    )
  }

  async function handleCreate(payload) {
    await createPromotion(payload)
    setToast(`Created "${payload.name}".`)
  }

  async function handleSave(id, patch) {
    await updatePromotion(id, patch)
    setToast('Promotion saved.')
  }

  async function handleDelete(id) {
    await deletePromotion(id)
    setToast('Promotion deleted.')
  }

  async function handleSetStatus(promo, status) {
    try {
      await setStatus(promo.id, status)
      setToast(`Status: ${status}.`)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[handleSetStatus]', e)
      setToast(`Could not update status: ${e?.message || 'try again.'}`)
    }
  }

  const isEmpty =
    grouped.live.length === 0 &&
    grouped.planned.length === 0 &&
    grouped.past.length === 0

  const draftBadgeCount = drafts.counts?.draft || 0

  return (
    <div className="min-h-full bg-white pb-12">
      <main className="mx-auto max-w-screen-sm">
        <HeroBriefCard
          tabKey="marketing"
          phase={hero.phase}
          brief={hero.brief}
          error={hero.error}
          generatedAt={hero.generatedAt}
          onGenerate={hero.regenerate}
        />

        {/* Header */}
        <section
          className="mx-4 mt-4 rounded-2xl border border-black/10 bg-white p-5 shadow-sm"
          aria-label="Marketing"
        >
          <div className="flex items-baseline justify-between gap-2">
            <div>
              <div className="text-xs font-medium text-black/55">
                Marketing
              </div>
              <p className="mt-1 text-xs text-black/60 leading-snug">
                Track promotions and let AI draft your social posts —
                weekly, on autopilot.
              </p>
            </div>
            {tab === 'promotions' && (
              <button
                type="button"
                onClick={() => setCreateOpen(true)}
                className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                + New promotion
              </button>
            )}
          </div>
        </section>

        {/* Sub-tab nav */}
        <div className="mx-4 mt-3 grid grid-cols-2 gap-1 rounded-lg border border-black/10 bg-black/[0.02] p-1">
          <button
            type="button"
            onClick={() => setTab('promotions')}
            className={`rounded-md px-3 py-2 text-sm font-medium ${
              tab === 'promotions' ? 'bg-white text-blue-700 shadow-sm' : 'text-black/55'
            }`}
          >
            Promotions
          </button>
          <button
            type="button"
            onClick={() => setTab('drafts')}
            className={`rounded-md px-3 py-2 text-sm font-medium ${
              tab === 'drafts' ? 'bg-white text-blue-700 shadow-sm' : 'text-black/55'
            }`}
          >
            Drafts
            {draftBadgeCount > 0 && (
              <span className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] ${
                tab === 'drafts' ? 'bg-blue-100 text-blue-700' : 'bg-blue-600 text-white'
              }`}>
                {draftBadgeCount}
              </span>
            )}
          </button>
        </div>

        {tab === 'promotions' && (
          <>
            {isEmpty && (
              <div className="mx-4 mt-6 rounded-xl border border-dashed border-black/15 px-4 py-8 text-center text-sm text-black/65">
                No promotions yet. Tap{' '}
                <span className="font-medium text-blue-600">+ New promotion</span>
                {' '}to log your first one — describe the offer, pick the channels,
                and the AI will draft your posts.
              </div>
            )}

            {grouped.live.length > 0 && (
              <PromoSection
                title="Live now"
                tone="emerald"
                promotions={grouped.live}
                onEdit={setEditingPromo}
                onGenerate={setGeneratingFor}
                onSetStatus={handleSetStatus}
              />
            )}

            {grouped.planned.length > 0 && (
              <PromoSection
                title="Planned"
                tone="blue"
                promotions={grouped.planned}
                onEdit={setEditingPromo}
                onGenerate={setGeneratingFor}
                onSetStatus={handleSetStatus}
              />
            )}

            {grouped.past.length > 0 && (
              <PromoSection
                title="Past"
                tone="muted"
                promotions={grouped.past}
                onEdit={setEditingPromo}
                onGenerate={setGeneratingFor}
                onSetStatus={handleSetStatus}
              />
            )}

            {/* Review reply assistant — Marketing tab also handles
                inbound (reviews) not just outbound (promotions). */}
            <ReviewReplyCard
              restaurantName={restaurant?.name}
              restaurantType={restaurant?.cuisine_type}
            />
          </>
        )}

        {tab === 'drafts' && (
          <MarketingDraftsInbox
            drafts={drafts.drafts}
            grouped={drafts.grouped}
            counts={drafts.counts}
            loading={drafts.loading}
            error={drafts.error}
            onRefresh={drafts.refresh}
            onApprove={drafts.approveDraft}
            onReject={drafts.rejectDraft}
            onEdit={drafts.editDraft}
            onMarkPosted={drafts.markPosted}
            onGenerateNow={({ force }) =>
              drafts.generateNow({ restaurantId: restaurant?.id, force })
            }
            onOpenSettings={() => setSettingsOpen(true)}
            hasSettings={!!drafts.settings && drafts.settings.enabled}
          />
        )}

        {/* AI ask bar — Marketing-scoped placeholder. */}
        <AskAiBar placeholder="Write a post about Tuesday's special…" />
      </main>

      <PromotionEditModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={handleCreate}
      />

      <PromotionEditModal
        open={!!editingPromo}
        onClose={() => setEditingPromo(null)}
        onSave={handleSave}
        onDelete={handleDelete}
        promotion={editingPromo}
      />

      <PromotionPostsModal
        open={!!generatingFor}
        onClose={() => setGeneratingFor(null)}
        promotion={generatingFor}
        restaurant={restaurant}
      />

      <MarketingSettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onSave={async (patch) => {
          await drafts.saveSettings(patch)
          setToast(patch.enabled ? 'Marketing automation enabled.' : 'Settings saved.')
        }}
        settings={drafts.settings}
        restaurantId={restaurant?.id}
      />

      <Toast message={toast} onDone={() => setToast('')} />
    </div>
  )
}

function PromoSection({ title, tone, promotions, onEdit, onGenerate, onSetStatus }) {
  const headerColor = tone === 'emerald' ? 'text-emerald-700'
    : tone === 'blue' ? 'text-blue-700'
    : 'text-black/55'
  const dotColor = tone === 'emerald' ? 'bg-emerald-500'
    : tone === 'blue' ? 'bg-blue-500'
    : 'bg-black/30'
  return (
    <section className="mx-4 mt-6" aria-label={title}>
      <div className="mb-2 flex items-baseline gap-2">
        <span className={`inline-block h-2 w-2 rounded-full ${dotColor}`} />
        <h2 className={`text-base font-medium ${headerColor}`}>
          {title}
          <span className="ml-2 text-xs font-normal text-black/55">
            {promotions.length}
          </span>
        </h2>
      </div>
      <ul className="overflow-hidden rounded-xl border border-black/10">
        {promotions.map((p) => (
          <PromoRow
            key={p.id}
            promotion={p}
            onEdit={onEdit}
            onGenerate={onGenerate}
            onSetStatus={onSetStatus}
            tone={tone}
          />
        ))}
      </ul>
    </section>
  )
}

function PromoRow({ promotion, onEdit, onGenerate, onSetStatus, tone }) {
  const channelLabels = (promotion.channels || []).map((c) => CHANNEL_NAME[c] || c)
  return (
    <li className="border-b border-black/5 px-4 py-3 last:border-b-0 hover:bg-blue-50/40">
      <div className="flex items-baseline justify-between gap-2">
        <button
          type="button"
          onClick={() => onEdit(promotion)}
          className="min-w-0 flex-1 text-left"
        >
          <div className="truncate text-base font-medium">
            {promotion.name}
            {promotion.status === 'cancelled' && (
              <span className="ml-2">
                <StatusPill tone={toneFor('promotion', 'cancelled')}>
                  {labelFor('promotion', 'cancelled')}
                </StatusPill>
              </span>
            )}
          </div>
          <div className="mt-0.5 truncate text-xs text-black/60">
            {promotion.starts_on}
            {promotion.ends_on && ` – ${promotion.ends_on}`}
            {channelLabels.length > 0 && ` · ${channelLabels.join(' · ')}`}
            {promotion.cost_estimate != null && (
              <> · est {formatMoney(promotion.cost_estimate)}</>
            )}
          </div>
        </button>
      </div>
      {promotion.description && (
        <div className="mt-1 line-clamp-2 text-xs text-black/55">
          {promotion.description}
        </div>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => onGenerate(promotion)}
          className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
        >
          ✨ Generate posts
        </button>
        {tone === 'blue' && promotion.status === 'planned' && (
          <button
            type="button"
            onClick={() => onSetStatus(promotion, 'live')}
            className="rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-sm font-medium text-emerald-700 hover:bg-emerald-50"
          >
            Mark live
          </button>
        )}
        {tone === 'emerald' && promotion.status !== 'completed' && (
          <button
            type="button"
            onClick={() => onSetStatus(promotion, 'completed')}
            className="rounded-lg border border-black/15 bg-white px-3 py-1.5 text-sm font-medium text-black/65 hover:bg-black/5"
          >
            Mark complete
          </button>
        )}
      </div>
    </li>
  )
}

const CHANNEL_NAME = {
  instagram: 'Instagram',
  facebook:  'Facebook',
  sms:       'SMS',
  email:     'Email',
  in_store:  'In-store',
}
